/**
 * Offline map packs: pull an AOR's tiles down *before* leaving signal.
 *
 * The service worker already serves any tile it has seen, which covers "I flew
 * this area yesterday". It does nothing for the real edge case: arriving in a
 * dead zone and needing a part of the map nobody has opened yet. This walks the
 * tile pyramid for an AOR and fetches every tile in it, which populates the same
 * cache the worker reads — so the map keeps working with the radio off.
 *
 * Fetching *is* the download. There is no second store to keep in sync and
 * nothing to invalidate: the worker's cache-first tile strategy means a warmed
 * tile and a browsed tile are the same entry, and clearing the cache clears
 * both.
 */

/** Zoom range a pack covers: wide enough to orient, tight enough to fly. */
export const PACK_MIN_ZOOM = 9;
export const PACK_MAX_ZOOM = 14;

/** Rough bytes per raster tile, measured against the OSM tiles this app uses. */
const BYTES_PER_TILE = 14_000;

/**
 * Refuse to start a pack bigger than this many tiles. A typo in a bounding box
 * should not silently try to download a continent onto a field laptop.
 */
export const MAX_PACK_TILES = 20_000;

export interface TileRef {
  z: number;
  x: number;
  y: number;
}

export interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface PackEstimate {
  tiles: number;
  bytes: number;
  tooLarge: boolean;
}

export interface PackProgress {
  done: number;
  total: number;
  failed: number;
}

function lonToX(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * 2 ** z);
}

function latToY(lat: number, z: number): number {
  // Web Mercator: clamp to the projection's limit, or a pole makes tan() blow
  // up and the tile index comes back NaN.
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const rad = (clamped * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z);
}

/** The tile index range covering `bounds` at one zoom, already clamped. */
function rangeAt(bounds: Bounds, z: number): { x1: number; x2: number; y1: number; y2: number } {
  const limit = 2 ** z - 1;
  const clamp = (n: number) => Math.max(0, Math.min(limit, n));
  const xs = [clamp(lonToX(bounds.west, z)), clamp(lonToX(bounds.east, z))];
  // y grows southward, so north gives the smaller index.
  const ys = [clamp(latToY(bounds.north, z)), clamp(latToY(bounds.south, z))];
  return {
    x1: Math.min(...xs),
    x2: Math.max(...xs),
    y1: Math.min(...ys),
    y2: Math.max(...ys),
  };
}

/**
 * How many tiles a pack would contain — arithmetic only.
 *
 * Never build the array to find this out. A world-sized box at zoom 14 is
 * hundreds of millions of tiles, and materialising them to call `.length`
 * exhausts the heap and takes the tab down before the operator is ever told
 * the area is too big.
 */
export function countTilesForBounds(
  bounds: Bounds,
  minZoom = PACK_MIN_ZOOM,
  maxZoom = PACK_MAX_ZOOM,
): number {
  let total = 0;
  for (let z = minZoom; z <= maxZoom; z += 1) {
    const { x1, x2, y1, y2 } = rangeAt(bounds, z);
    total += (x2 - x1 + 1) * (y2 - y1 + 1);
  }
  return total;
}

/**
 * Every tile covering `bounds` across the zoom range, inclusive.
 *
 * Refuses rather than allocates when the area is absurd; callers are expected
 * to have checked `estimatePack` first, and this is the backstop that keeps a
 * mistake from becoming a dead tab.
 */
export function tilesForBounds(
  bounds: Bounds,
  minZoom = PACK_MIN_ZOOM,
  maxZoom = PACK_MAX_ZOOM,
): TileRef[] {
  const count = countTilesForBounds(bounds, minZoom, maxZoom);
  if (count > MAX_PACK_TILES) {
    throw new RangeError(
      `Pack would contain ${count.toLocaleString()} tiles, over the ${MAX_PACK_TILES.toLocaleString()} limit.`,
    );
  }
  const tiles: TileRef[] = [];
  for (let z = minZoom; z <= maxZoom; z += 1) {
    const { x1, x2, y1, y2 } = rangeAt(bounds, z);
    for (let x = x1; x <= x2; x += 1) {
      for (let y = y1; y <= y2; y += 1) {
        tiles.push({ z, x, y });
      }
    }
  }
  return tiles;
}

/** A square box of `radiusKm` around a point, in degrees. */
export function boundsAround(lat: number, lon: number, radiusKm: number): Bounds {
  const dLat = radiusKm / 111;
  // Longitude degrees shrink toward the poles; using a flat number would make
  // the box far too wide at high latitude and download a lot of ocean.
  const dLon = radiusKm / (111 * Math.max(0.01, Math.cos((lat * Math.PI) / 180)));
  return {
    north: lat + dLat,
    south: lat - dLat,
    east: lon + dLon,
    west: lon - dLon,
  };
}

export function estimatePack(bounds: Bounds, minZoom?: number, maxZoom?: number): PackEstimate {
  const tiles = countTilesForBounds(bounds, minZoom, maxZoom);
  return { tiles, bytes: tiles * BYTES_PER_TILE, tooLarge: tiles > MAX_PACK_TILES };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Default template only. Real callers pass the server's basemap template
 * (api/basemap.ts) so warmed tiles match the URLs the map browses.
 */
export const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export function tileUrl(tile: TileRef, template = TILE_URL): string {
  return template
    .replace('{z}', String(tile.z))
    .replace('{x}', String(tile.x))
    .replace('{y}', String(tile.y));
}

/**
 * Fetch every tile, reporting progress. Bounded concurrency: the tile host is
 * a shared public service and a thousand parallel requests is abuse that gets
 * an IP blocked, which would take the *online* map down too.
 */
export async function downloadPack(
  tiles: TileRef[],
  onProgress: (progress: PackProgress) => void,
  options: { concurrency?: number; signal?: AbortSignal; template?: string } = {},
): Promise<PackProgress> {
  const concurrency = options.concurrency ?? 6;
  const progress: PackProgress = { done: 0, total: tiles.length, failed: 0 };
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < tiles.length) {
      if (options.signal?.aborted) return;
      const tile = tiles[cursor++]!;
      try {
        // Same request the map makes, so the service worker stores it under
        // the key the map will later look for.
        const response = await fetch(tileUrl(tile, options.template), {
          mode: 'cors',
          signal: options.signal,
        });
        if (!response.ok) progress.failed += 1;
      } catch {
        // A single missing tile is a hole in the pack, not a failed download.
        if (options.signal?.aborted) return;
        progress.failed += 1;
      }
      progress.done += 1;
      onProgress({ ...progress });
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, tiles.length) }, worker));
  return progress;
}
